import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { accountsApi } from '../../api/accounts'
import { contactsApi } from '../../api/contacts'
import { reportsApi } from '../../api/reports'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate, formatMoney } from '../../lib/format'
import { scopeWords } from '../../lib/orgs'
import { kindBlurb, kindClass, kindLede, kindWord, segmentClass, segmentWord } from '../../lib/accounts'
import type { AccountSummary, CoverageLine, HealthGauge } from '../../types/accounts'
import type { Engagement } from '../../types/deals'
import type { ContactView } from '../../types/contacts'
import { voteTier } from '../../lib/contacts'
import { Avatar } from '../../components/Avatar'
import { Correct } from '../../components/Correct'
import { LineIcon } from '../../components/LineIcon'
import { PriorWorkCard } from '../../components/PriorWork'
import { VoteBadge, VoteButtons } from '../../components/VoteButtons'
import { ContactDrawer } from '../Contacts/ContactDrawer'
import { AccountGlance } from './AccountGlance'
import { AccountHealth } from './AccountHealth'
import { AccountInfluenceMap } from './AccountInfluenceMap'
import { Newsfeed } from './Newsfeed'
import { Roadmap } from './Roadmap'
import { WaysIn } from './WaysIn'
import './accounts.css'

// Account planning (PM-008), on the firm's Gryphon deck template: each account on one page. Header, then
// the glance (the best way in and the numbers), then the influence map as a graph with the ways in under
// it, beside the health dashboard and the newsfeed in the corner; then the live pipeline, who covers whom,
// and the people. Coverage is read for the org the sidebar has selected — the collision guardrail is an
// org-wide question, and the kicker names the org it was answered for.

const STAGE_WORDS: Record<string, string> = {
  lead: 'Lead', opportunity: 'Opportunity', proposal: 'Proposal out', negotiation: 'Negotiating', won: 'Won', lost: 'Lost', delivery: 'In delivery', dormant: 'Gone quiet', none: '—',
}
/** The stages the pipeline table shows by default (a display filter; the counts come from the API). */
const OPEN = new Set(['lead', 'opportunity', 'proposal', 'negotiation'])
/** Where each gauge's evidence lives on the page. */
const EVIDENCE_ANCHOR: Record<HealthGauge['evidence'], string> = { people: 'plan-people', 'ways-in': 'plan-ways-in', newsfeed: 'plan-newsfeed', pipeline: 'plan-pipeline', map: 'plan-map' }

export function Accounts() {
  const spoke = useSpoke()
  const q = useQuery({ queryKey: ['accounts', spoke], queryFn: () => accountsApi.list(spoke), staleTime: 15_000 })
  const rows = q.data?.accounts ?? []
  // The firm's own suppliers and the threads nobody could place. Not hidden — that would be a lie about
  // what is on file — but under one collapsed section, so the list above is the pipeline.
  const vendors = q.data?.vendors ?? []
  const names = q.data?.ownerNames ?? {}
  const scope = scopeWords(q.data?.scope)
  const totals = useMemo(() => ({
    pipeline: rows.reduce((n, a) => n + a.pipeline, 0),
    open: rows.reduce((n, a) => n + a.openPursuits, 0),
  }), [rows])

  return (
    <section className="page accounts">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link>{scope ? ` · ${scope}` : ''}</p>
          <h1>Account Plans</h1>
          <p className="lede">Every account you are pursuing, on one page each: the influence map and the ways in, the health dashboard, the newsfeed, the live pipeline, who covers whom, and the contacts. Read out of the firm's mail, on your account-planning template.</p>
        </div>
      </header>

      {q.isSuccess && rows.length > 0 && (
        <div className="deal-totals">
          <div className="stat accent"><strong>{rows.length}</strong><span className="muted">accounts</span></div>
          <div className="stat"><strong>{totals.open}</strong><span className="muted">open pursuits</span></div>
          <div className="stat"><strong>{totals.pipeline > 0 ? formatMoney(totals.pipeline) : '—'}</strong><span className="muted">pipeline named</span></div>
        </div>
      )}

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && <div className="refusal"><strong>Unavailable</strong><p>{q.error.message}</p></div>}
      {q.isSuccess && rows.length === 0 && <div className="card"><p className="muted">No accounts yet. Read your threads for leads and opportunities; the companies you pursue become accounts here.</p></div>}

      {rows.length > 0 && (
        <table className="grid accounts-grid">
          <thead>
            <tr><th>Account</th><th>Sector</th><th className="num">Pursuits</th><th className="num">Pipeline</th><th>Coverage</th><th>News</th><th>Last activity</th><th className="correct-col"><span className="visually-hidden">Not right?</span></th></tr>
          </thead>
          <tbody>
            {rows.map((a) => <AccountRow key={a.companyId} a={a} names={names} />)}
          </tbody>
        </table>
      )}

      {vendors.length > 0 && (
        <details className="card vendors-aside">
          <summary>
            <strong>Vendors &amp; other</strong> <span className="tag">{vendors.length}</span>
            <span className="muted small">Suppliers who sell to the firm, and threads nobody could place. Not pursuits.</span>
          </summary>
          <table className="grid accounts-grid">
            <thead>
              <tr><th>Account</th><th>Sector</th><th className="num">Pursuits</th><th className="num">Pipeline</th><th>Coverage</th><th>News</th><th>Last activity</th><th className="correct-col"><span className="visually-hidden">Not right?</span></th></tr>
            </thead>
            <tbody>
              {vendors.map((a) => <AccountRow key={a.companyId} a={a} names={names} />)}
            </tbody>
          </table>
        </details>
      )}
    </section>
  )
}

function AccountRow({ a, names }: { a: AccountSummary; names: Record<string, string> }) {
  const navigate = useNavigate()
  return (
    <tr className="account-row" onClick={() => navigate(`/accounts/${encodeURIComponent(a.companyId)}`)}>
      <td>
        <strong>{a.name}</strong>
        {/* what it IS to the firm, said even when nobody has decided: a blank here read as "prospect" */}
        <span className={`tag kind ${kindClass(a.kind)}`} title={kindBlurb(a.kind)}>{kindWord(a.kind)}</span>
        {a.domain && <span className="muted small">{a.domain}</span>}
      </td>
      <td>{a.sector ?? <span className="muted">—</span>}{a.tier && <span className="muted small">{a.tier}</span>}</td>
      <td className="num"><strong>{a.openPursuits}</strong><span className="muted small">of {a.pursuits}</span></td>
      <td className="num">{a.pipeline > 0 ? formatMoney(a.pipeline) : <span className="muted">—</span>}</td>
      <td>{a.ownerIds.slice(0, 3).map((o) => names[o] ?? o).join(', ')}{a.ownerIds.length > 3 ? ` +${a.ownerIds.length - 3}` : ''}</td>
      <td>{a.signals > 0 ? <span className="tag sig watch">{a.signals}</span> : <span className="muted small">—</span>}</td>
      <td>{a.lastActivity ? formatDate(a.lastActivity) : '—'}</td>
      <td className="correct-col">
        <Correct kind="company-kind" entityId={a.companyId} entityLabel={a.name} compact />
      </td>
    </tr>
  )
}

export function AccountPlanPage() {
  const spoke = useSpoke()
  const navigate = useNavigate()
  const { companyId = '' } = useParams()
  const q = useQuery({ queryKey: ['account', spoke, companyId], queryFn: () => accountsApi.get(spoke, companyId), staleTime: 15_000 })
  const plan = q.data?.plan
  const names = q.data?.ownerNames ?? {}
  const owner = (id: string) => names[id] ?? id
  const brief = useMutation({ mutationFn: () => reportsApi.brief(spoke, companyId) })
  // the brief the night already wrote ahead of a meeting (REQ-074): shown on arrival, the button writes a fresh one
  const stored = useQuery({ queryKey: ['account-brief', spoke, companyId], queryFn: () => reportsApi.storedBrief(spoke, companyId), staleTime: 60_000, retry: false })
  const shown = brief.data ?? stored.data
  const photos = useMutation({ mutationFn: () => contactsApi.findPhotos(spoke), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })
  const [open, setOpen] = useState<string | null>(null)
  const [allPursuits, setAllPursuits] = useState(false)
  const [flash, setFlash] = useState<string | null>(null)

  const onEvidence = useCallback((evidence: HealthGauge['evidence']) => {
    const id = EVIDENCE_ANCHOR[evidence]
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setFlash(id)
    window.setTimeout(() => setFlash((f) => (f === id ? null : f)), 1500)
  }, [])
  const flashClass = (id: string) => (flash === id ? ' flash' : '')

  if (q.isPending) return <p className="muted centered">Loading…</p>
  if (q.isError || !plan) return <section className="page"><div className="refusal"><strong>Unavailable</strong><p>{q.error?.message ?? 'No such account.'}</p><p><Link to="/accounts">Back to accounts</Link></p></div></section>

  const inf = plan.influence
  // the API ranks by the tie; the rep's vote comes first (REQ-123) — a stable sort keeps the tie order within a vote
  const people = plan.contacts.filter((c) => c.person.role !== 'colleague').sort((a, b) => voteTier(a) - voteTier(b))
  const { openPursuits, pipeline } = plan
  const withPhoto = people.filter((c) => c.person.photoUrl).length

  return (
    <section className="page account-plan">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/accounts">Account Plans</Link>{plan.company.domain ? ` · ${plan.company.domain}` : ''}{scopeWords(q.data?.scope) ? ` · ${scopeWords(q.data?.scope)}` : ''}</p>
          <h1>{plan.company.name}</h1>
          <p className="lede">
            <span className={`tag kind ${kindClass(plan.company.kind)}`} title={kindBlurb(plan.company.kind)}>{kindWord(plan.company.kind)}</span>
            {' '}<span className="muted" title={q.data?.kindBasis ? `decided by: ${q.data.kindBasis}` : undefined}>{kindLede(plan.company.kind, q.data?.kindWhy)}</span>{' '}
            {/* which MARKET it sits in — a sponsor, a lender, a corporate, a bank — said as not yet known until decided (REQ-075) */}
            <span className={`tag kind segment ${segmentClass(plan.company.segment)}`} title="Which market this organisation sits in; the briefs phrase for it.">{segmentWord(plan.company.segment)}</span>{' · '}
            {[plan.company.sector, plan.company.tier].filter(Boolean).join(' · ')}
            {(plan.company.sector || plan.company.tier) ? ' · ' : ''}
            {people.length} {people.length === 1 ? 'person' : 'people'} known, {plan.engagements.length} pursuit{plan.engagements.length === 1 ? '' : 's'} on file. The map reads left to right: who at the firm holds a tie, who they reach, and who decides.
          </p>
          {/* THE ORGANISATION GRAPH (PL-210): whose portfolio it is in and what it is part of, each a link to that account */}
          {(plan.sponsorName || plan.parentName) && (
            <p className="lede org-graph">
              {plan.sponsorName && plan.sponsorCompanyId && <span className="org-line">A portfolio company of <Link to={`/accounts/${encodeURIComponent(plan.sponsorCompanyId)}`}>{plan.sponsorName}</Link></span>}
              {plan.sponsorName && plan.parentName ? ' · ' : ''}
              {plan.parentName && plan.company.parentCompanyId && <span className="org-line">Part of <Link to={`/accounts/${encodeURIComponent(plan.company.parentCompanyId)}`}>{plan.parentName}</Link></span>}
            </p>
          )}
        </div>
        <div className="actions">
          <button className="button" onClick={() => brief.mutate()} disabled={brief.isPending} title="A meeting-prep brief from the relationship, pursuits, and signals.">{brief.isPending ? 'Preparing…' : shown ? 'Prep brief again' : 'Prep brief'}</button>
          <button className="button icon" onClick={() => photos.mutate()} disabled={photos.isPending} title={`Look every contact up for a published profile picture. ${withPhoto} of ${people.length} here have one.`}><LineIcon name="photo" size={15} />{photos.isPending ? 'Starting…' : 'Find photos'}</button>
          <Correct kind="company-kind" entityId={companyId} entityLabel={plan.company.name} currentValue={plan.company.kind ?? null} label="Wrong kind of company?" />
          <Correct kind="company-segment" entityId={companyId} entityLabel={plan.company.name} currentValue={plan.company.segment ?? null} label="Wrong market?" />
          <Correct kind="account-parent" entityId={companyId} entityLabel={plan.company.name} currentValue={plan.company.parentCompanyId ?? null} label="Owned by someone else?" />
          <Link className="button" to={`/workbench/${encodeURIComponent(companyId)}`} title="Everyone on file around this account, a conversation that moves the draft, and a message that leaves for your Outbox.">Open in workbench</Link>
          <Link className="button" to="/contacts">Contacts</Link>
        </div>
      </header>
      {photos.isError && <p className="error">{photos.error instanceof RefusedError ? photos.error.refusal.message : photos.error.message}</p>}
      {shown && (
        <div className="card narrative">
          <p className="kicker">Meeting prep brief{shown.modelWritten ? '' : ' (from the data; no model configured)'}{shown.writtenAt ? ` · written ${formatDate(shown.writtenAt)}${shown.forMeeting ? ' ahead of your meeting' : ''}` : ''}</p>
          <pre className="narrative-text">{shown.text}</pre>
        </div>
      )}

      <AccountGlance plan={plan} ownerNames={names} onOpen={setOpen} />

      <div className="plan-body">
        <div className="plan-main">
          <div id="plan-map" className={`card plan-map-card${flashClass('plan-map')}`}>
            <div className="card-head">
              <div>
                <h2>Influence map</h2>
                <p className="muted">The firm's people on the left hold the ties (line weight is strength); everyone known sits in the middle; the people to reach are on the right. A dashed arrow is an introduction; a dashed red halo means no path yet. Hover for the tie, click to open the contact.</p>
              </div>
            </div>
            {people.length === 0 ? (
              <div className="ways-empty"><strong>No people known at this account yet.</strong><p className="muted">Read your threads for leads and opportunities; the people written to appear here with the ties behind them.</p></div>
            ) : (
              <AccountInfluenceMap contacts={plan.contacts} influence={inf} ownerNames={names} onOpen={setOpen} />
            )}
          </div>
          <div id="plan-ways-in" className={flashClass('plan-ways-in')}>
            <WaysIn routes={inf.routes} contacts={plan.contacts} ownerNames={names} onOpen={setOpen} />
          </div>
          <div id="plan-roadmap" className={flashClass('plan-roadmap')}>
            <Roadmap spoke={spoke} companyId={companyId} companyName={plan.company.name} />
          </div>
        </div>
        <div className="plan-side">
          <div id="plan-health" className={flashClass('plan-health')}><AccountHealth health={plan.health} onEvidence={onEvidence} companyId={companyId} companyName={plan.company.name} /></div>
          <div id="plan-newsfeed" className={flashClass('plan-newsfeed')}><Newsfeed signals={plan.signals} contacts={plan.contacts} engagements={plan.engagements} onOpenPerson={setOpen} /></div>
          <div id="plan-prior-work"><PriorWorkCard spoke={spoke} companyId={companyId} companyName={plan.company.name} /></div>
        </div>
      </div>

      <div id="plan-pipeline" className={`card plan-section${flashClass('plan-pipeline')}`}>
        <div className="card-head">
          <div>
            <h2>State of relationship</h2>
            <p className="muted">The live pipeline at this account: {openPursuits} open, {plan.engagements.length} on file{pipeline > 0 ? `, ${formatMoney(pipeline)} named` : ''}.</p>
          </div>
          {plan.engagements.length > openPursuits && (
            <button className="button small" onClick={() => setAllPursuits((v) => !v)}>{allPursuits ? `Open only (${openPursuits})` : `Show all ${plan.engagements.length}`}</button>
          )}
        </div>
        {plan.engagements.length === 0 ? <p className="muted">No pursuits on file.</p> : (
          <table className="grid plan-grid">
            <thead><tr><th>What is being pursued</th><th>Stage</th><th>Service line</th><th className="num">Amount</th><th>Next step</th><th>Lead</th></tr></thead>
            <tbody>
              {(allPursuits || openPursuits === 0 ? plan.engagements : plan.engagements.filter((e) => OPEN.has(e.stage))).map((e: Engagement) => (
                <tr key={e.engagementId} className={`stage-${e.stage}`}>
                  <td><strong>{e.subject}</strong><span className="muted small">{e.summary}</span></td>
                  <td><span className={`tag stage ${e.stage}`}>{STAGE_WORDS[e.stage] ?? e.stage}</span></td>
                  <td>{e.serviceLine ?? <span className="muted">—</span>}</td>
                  <td className="num">{e.amount ? formatMoney(e.amount) : <span className="muted">—</span>}</td>
                  <td>{e.nextStep ? <>{e.nextStep}<span className="muted small">{e.nextStepOwner === 'me' ? 'you owe it' : e.nextStepOwner === 'them' ? 'they owe it' : ''}{e.nextStepBy ? ` · by ${formatDate(e.nextStepBy)}` : ''}</span></> : <span className="muted">—</span>}</td>
                  <td>{owner(e.ownerId)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="plan-cols">
        <div className="card">
          <div className="card-head"><div><h2>Coverage map</h2><p className="muted">Who at the firm covers this account, and how warm their ties run.</p></div></div>
          {plan.coverage.length === 0 ? <p className="muted">No coverage recorded.</p> : (
            <ul className="plain coverage">
              {plan.coverage.map((c: CoverageLine) => {
                const pct = Math.round(c.strength * 100)
                const band = pct >= 60 ? 'strong' : pct >= 30 ? 'warm' : 'faint'
                const ways = inf.routes.filter((r) => r.ownerId === c.ownerId).length
                return (
                  <li key={c.ownerId}>
                    <strong>{owner(c.ownerId)}</strong>
                    <span className="strength-bar" title={`average tie ${pct}%`}><span className={`strength-fill ${band}`} style={{ width: `${Math.max(4, pct)}%` }} /></span>
                    <span className="muted small">{c.contacts} contact{c.contacts === 1 ? '' : 's'} · {c.engagements} pursuit{c.engagements === 1 ? '' : 's'} · {ways} way{ways === 1 ? '' : 's'} in · average tie {pct}%</span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div id="plan-people" className={`card${flashClass('plan-people')}`}>
          <div className="card-head"><div><h2>People</h2><p className="muted">{people.length} known, ranked by the tie; {withPhoto} with a photo. Persona is the part they play on the pursuit.</p></div></div>
          {people.length === 0 ? <p className="muted">No contacts on file.</p> : (
            <ul className="plain contacts-list">
              {people.slice(0, 24).map((c: ContactView) => {
                const pct = Math.round((c.person.relationshipStrength ?? 0) * 100)
                const band = pct >= 60 ? 'strong' : pct >= 30 ? 'warm' : 'faint'
                const t = inf.ties.find((x) => x.personId === c.person.personId)
                return (
                  <li key={c.person.personId}>
                    <Avatar name={c.person.name} photoUrl={c.person.photoUrl} persona={c.person.persona} role={c.person.role} size={34} />
                    <div>
                      <div className="contact-name">
                        <button className="link-button" onClick={() => setOpen(c.person.personId)}>{c.person.name}</button>
                        {c.person.persona && c.person.persona !== 'other' && <span className={`tag persona ${c.person.persona}`}>{c.person.persona}</span>}
                        <VoteBadge vote={c.vote} />
                        {inf.unreachedIds.includes(c.person.personId) && <span className="tag sig high">no path</span>}
                        {/* who they assist, who assists them (PL-212): the link read from a signature, both sides */}
                        {(q.data?.links ?? []).filter((l) => l.kind === 'assistant-of' && l.fromPersonId === c.person.personId).map((l) => (
                          <span key={`a-${l.toPersonId}`} className="tag link-assistant" title="Read from their signature: they keep this person's diary">assists {(plan.contacts.find((x) => x.person.personId === l.toPersonId)?.person.name ?? l.toPersonId)}</span>
                        ))}
                        {(q.data?.links ?? []).filter((l) => l.kind === 'assistant-of' && l.toPersonId === c.person.personId).map((l) => (
                          <span key={`b-${l.fromPersonId}`} className="tag link-assistant" title="Their assistant, read from a signature: the way to their diary">assisted by {(plan.contacts.find((x) => x.person.personId === l.fromPersonId)?.person.name ?? l.fromPersonId)}</span>
                        ))}
                        {(q.data?.links ?? []).filter((l) => l.kind === 'introduced' && l.toPersonId === c.person.personId).map((l) => (
                          <span key={`i-${l.fromPersonId}`} className="tag link-introduced" title="A referral on file: this person introduced them">introduced by {(plan.contacts.find((x) => x.person.personId === l.fromPersonId)?.person.name ?? l.fromPersonId)}</span>
                        ))}
                        {c.signals.some((s) => s.type === 'dormant') && <span className="tag sig watch">quiet</span>}
                        {c.signals.some((s) => s.type === 'job-change') && <span className="tag sig watch">moved</span>}
                      </div>
                      <span className="muted small">{[c.person.title, t && t.touches > 0 ? `${t.touches} touch${t.touches === 1 ? '' : 'es'}` : null, t && t.meetings > 0 ? `${t.meetings} meeting${t.meetings === 1 ? '' : 's'}` : null, c.lastActivity ? `last ${formatDate(c.lastActivity)}` : null].filter(Boolean).join(' · ')}</span>
                    </div>
                    <div className="contact-right">
                      <span className="strength-bar" title={`${pct}%`}><span className={`strength-fill ${band}`} style={{ width: `${Math.max(4, pct)}%` }} /></span>
                      <span className="mono">{pct}%</span>
                      <Correct kind="contact-role" entityId={c.person.personId} entityLabel={c.person.name} currentValue={c.person.role} compact />
                      <VoteButtons spoke={spoke} personId={c.person.personId} vote={c.vote} compact />
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      {open && <ContactDrawer spoke={spoke} personId={open} onClose={() => setOpen(null)} />}
    </section>
  )
}
